import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import AutoSuggest from 'react-autosuggest';
import classNames from 'classnames';
import { quoteFormSelector } from 'ctm-selectors';
import { inputUIComponent } from 'ctm-css-themes';
import {
    apiAction,
    nestFlatQuery,
    interpolateString,
    rulesCache,
    pageContext,
    get,
    applyVerticalStyles
} from 'ctm-util-functions';

import { LoadingAnimation } from '../../loading_animation/loading_animation';
import * as actions from '../actions';
import { debounce, getDisabledProp, getComponentStateSuffix, getRecordingMaskClass } from '../utils';
import styles from './auto_complete_combo.css';

class _AutoCompleteCombo extends Component {

    constructor(props) {
        super(props);
        const { value, options } = props;

        this.state = {
            loading: false,
            optionSelected: !!value,
            value: value || '',
            options: options || []
        };
        this.inputUIComponent = applyVerticalStyles(inputUIComponent, 'input');
        this.config = get(props, 'schema.config');
        if (!this.config) {
            throw Error('You have not specified a config object for this component.');
        }
    }

    /**
     * Setup a debounced function for loading options with a configurable timeout.
     */
    componentWillMount = () => {
        const { schema } = this.props;
        this.debouncedLoadOptions = debounce((value) => {
            const { optionSelected } = this.state;
            // prevent unnecessary request fetch
            if (!optionSelected) this.loadOptions(value);
        }, schema.field.debounceTime);
    };

    /**
     * If an input has transitioned from disabled to enabled and has autoFocus supplied then focus input
     * @param {Object} prevProps - The previous set of props
     */
    componentDidUpdate = (prevProps) => {
        const { schema, payloadArgs } = this.props;
        if (schema.field.autoFocus && getDisabledProp(prevProps).disabled && !getDisabledProp(this.props).disabled) {
            this.autosuggest.input.focus();
        } else if (this.havePayloadArgsChanged(payloadArgs, prevProps.payloadArgs)) {
            // this handles the case where the user has entered a postcode and a valid address, but then decides to change the
            // postcode, which invalidates the address, so we reset it on component and app state and refocus the input
            this.clearValue();
        }
    };

    /**
     * Lifecycle method to update state for new props
     * @param {Object} nextProps - The incoming set of props
     */
    componentWillReceiveProps = (nextProps) => {
        const { value } = this.props;
        // This is mainly for syncing the value when pre-filling fields with profile data
        if (nextProps.value && value !== nextProps.value) {
            this.setState({ value: nextProps.value });
        }
    };

    /**
     * Clear the value for this component on the component and app states and re-focus the input
     */
    clearValue = () => {
        const { dispatch, schema } = this.props;
        this.setState({ value: '' });
        dispatch(
            actions.setValue(
                schema.identifier,
                '',
                schema.stateKey
            )
        );
        dispatch(
            actions.resetTouches(
                [schema.identifier],
                schema.stateKey
            )
        );
        // Clear app state values if there are no options to select
        if (!(schema.identifier === 'helpers.carModelInputPrefillVerify')) {
            this.setValuesFromPayload();
            this.autosuggest.input.focus();
        }
    };

    /**
     * Returns true if new payload args have been supplied to this component after an update
     * This is used so we can clear the autocomplete if another value that it uses for it's API call has changed
     * @param {Object} payloadArgs - The current set of payloadArgs - from this.props.payloadArgs
     * @param {Object} prevPayloadArgs - The previous set of payloadArgs
     * @returns {Boolean} - True if any of the payloadArgs have changed as a result of an update
     */
    havePayloadArgsChanged = (payloadArgs = {}, prevPayloadArgs) => {
        for (const key of Object.keys(payloadArgs)) {
            if (payloadArgs[key] !== prevPayloadArgs[key]) {
                return true;
            }
        }
        return false;
    };
    
    /**
     * Set the values on state from API response payload. This can also be used to clear the mapped payload values on state
     * for this component by passing in payload={}
     * @param {Object} payload - The payload from the response of the configured onChange API schema
     */
    setValuesFromPayload = (payload = {}) => {
        const { schema, dispatch } = this.props;
        const payloadStateMap = this.config.stateKeyMap;
        // Iterate over the mapped keys -{stateKey: payloadKey} - so that we can clear the stateKeys if no payload
        // is provided
        const stateValues = Object.keys(payloadStateMap).reduce((acc, key) => {
            const fullKey = this.config.targetIdentifier ? `${this.config.targetIdentifier}.${key}` : key;
            if (get(schema.field, 'dontReset', []).includes(key) && !payload[payloadStateMap[key]]) {
                return acc;
            }
            acc[fullKey] = payload[payloadStateMap[key]] || null;
            return acc;
        }, {});

        // Set the mapped payload values on app state
        dispatch(
            actions.setValues(
                nestFlatQuery(stateValues),
                schema.stateKey
            )
        );

        // Copy the state values to another part of the state tree if required - eg residentialAddress === postalAddress
        const setValuesRule = get(this.props, 'rules.setValuesFromState', {});
        if (setValuesRule.result) {
            const elementRuleProps = rulesCache.getElementRuleForId(setValuesRule.id).props;
            dispatch(actions.setFormValuesFromState(elementRuleProps, schema.stateKey));
        }
    };

    /**
     * Called when the user selects one of the displayed options.
     * If the option is empty and there is a manualTrigger defined, dispatch the action to show the manual inputs
     * If an option is selected, set the label to app state so it can be displayed if the component re-mounts
     * Set the selected value or payload to app state
     * If an 'onSetValue' API schema is defined, dispatch the API action with the suggestion payload.
     * @param {Object} event - The click event from selecting a suggestion
     * @param {Object} suggestion - The selected suggestion
     */
    onSuggestionSelected = (event, { suggestion }) => {
        const { dispatch, schema } = this.props;
        // If the value is empty string (i.e. the 'no results returned' message) and we have an option to enter the info manually,
        // then fire the action that enables the manual entry of the info
        const manualTrigger = get(this.config, 'helpers.triggerManualEntry', false);
        if (suggestion.value === '' && manualTrigger) {
            dispatch(
                actions.setValue(
                    manualTrigger,
                    'Y',
                    schema.stateKey
                )
            );
            return;
        }

        // Set the label to state so it can be reused if this component re-mounts
        dispatch(
            actions.setValue(
                schema.identifier,
                suggestion.label,
                schema.stateKey
            )
        );

        this.setState({ optionSelected: true, value: suggestion.label }, () => {
            // have to set it back to null here as the setValuesFromSuggestion looks for null...
            if (this.config.stateKeyMap) {
                this.setValuesFromPayload(suggestion.value);
            } else if (this.config.valueStateKey) {
                dispatch(
                    actions.setValue(
                        this.config.valueStateKey,
                        suggestion.value,
                        schema.stateKey
                    )
                );
            }
            if (get(this.props, 'schema.api.onSetValue') && suggestion.value) {
                // Dispatch the API action for the onSetValue event.
                dispatch(this.getApiAction('onSetValue', suggestion.value.payload));
            }
        });
    };

    /**
     * Return an api action for the given event type
     * @param  {String}  actionType       - The type of api action to construct - onChange or onSetValue
     * @param  {Array}  inputValue       - RESTful args for the request - if dynamic and not loaded from schema
     * @returns {Object|null}      The action to dispatch, or null if there is no config for eventType for this component.
     */
    getApiAction = (actionType, inputValue) => {
        const { schema, payloadArgs } = this.props;
        const { api } = schema;
        if (!api) {
            throw new Error(`Action Type ${actionType} was not specified in your AutoCompleteCombo api schema.`);
        }
        const apiSchema = api[actionType];
        const restArgs = apiSchema.type === 'GET' ? [{ value: inputValue }] : [];
        const postArgs = apiSchema.type === 'POST' ? { [apiSchema.requestPayloadValueName]: inputValue, ...payloadArgs || {} } : {};
        const pageState = pageContext.getProps().pageState;
        const action = apiAction(apiSchema, pageState, restArgs, apiSchema.baseUrlName, postArgs);
        action.responseFunc = (response) => {
            this.setOptionsFromApiResponse(response);
        };
        return action;
    };

    /**
     * Load the options to display based on the current value entered into the combo box. If options are available
     * on this.props, find the options that start with the input and display these. Otherwise, make an API call based on
     * this.schema.api.onChange and display the returned options.
     * @param  {Object} inputValue - The current input value Object from the combo box
     * @param  {String} inputValue.value - The current input string value from the combo box
     */
    loadOptions = ({ value }) => {
        const { dispatch } = this.props;
        const noResultsMessage = get(this.props, 'schema.field.noResultsMessage', false);
        if (value === '' || value === noResultsMessage) {
            return;
        }

        if (this.shouldRenderSuggestions(value)) {
            if (get(this.props, 'options.length')) {
                this.setOptionsFromProps(value);
            } else {
                this.setState({ loading: true });
                dispatch(this.getApiAction('onChange', value));
            }
        } else {
            this.setState({ loading: false });
        }
    };

    /**
     * Load and set the options from this.props.options, filtering out any options with labels that don't start with `value`
     * @param {String} value - The current value of the AutoSuggest input
     */
    setOptionsFromProps = (value) => {
        const { options, schema } = this.props;
        const finalOptions = schema.config.optionsPersist && value === '' ? options : options.reduce((acc, option) => {
            if (typeof option.label === 'string' && option.label.toLowerCase().startsWith(value)) {
                acc.push(option);
            }
            return acc;
        }, []);
        this.setState({ options: finalOptions });
    };

    /**
     * Set the Options for the auto-complete selector.
     * @param  {Object} response - response to the api request.
     */
    setOptionsFromApiResponse = (response) => {
        const options = [];
        if (response && response.length > 0) {
            for (const result of response) {
                options.push({
                    label: interpolateString(this.config.labelTemplate)(result),
                    value: result
                });
            }
        } else if (response && response.length === 0) {
            options.push({
                label: get(this.props, 'schema.field.noResultsMessage', ''),
                id: `autocomplete.noResults.${get(this.props, 'schema.identifier', '')}`,
                value: '' // this has to be an empty string due to the internals of the AutoSuggest component
            });
            // Clear app state values if there are no options to select
            this.setValuesFromPayload();
        }
        this.setState({ options });
    };

    /**
     * Get the autocomplete suggestion value
     * @param {Object} opt - the autocomplete option
     * @returns {String} returns autocomplete option label
     */
    getSuggestionValue = (opt) => {
        return opt.label;
    };

    /**
     * Renders the autocomplete suggestion
     * @param {Object} opt - the autocomplete option
     * @returns {Node} returns autocomplete suggestion span
     */
    renderSuggestion = (opt) => {
        return (<span id={opt.id}>{opt.label}</span>);
    };

    /**
     * onChange handler for the text input in AutoSuggest, sets newValue to the app state
     * @param  {Object} event    The onChange event
     * @param  {String} newValue The new value of the input
     */
    onChange = (event, { newValue }) => {
        const { dispatch, schema } = this.props;
        const newState = { optionSelected: false };
        const noResultsMessage = get(this.props, 'schema.field.noResultsMessage', false);
        // this has to be a string due to the internals of the AutoSuggest component
        newState.value = newValue === noResultsMessage ? '' : newValue;

        if (newValue === '') {
            newState.loading = false;
            dispatch(
                actions.setValue(
                    schema.identifier,
                    null,
                    schema.stateKey
                )
            );
        }
        this.setState(newState);
    };

    /**
     * Only render when the length is X or more characters 'after'
     * the string has been sanitised
     * @param {String} value The value entered into the input.
     * @returns {boolean} Return true to render suggestions or false to halt.
     */
    shouldRenderSuggestions = (value) => {
        const { minChars = false } = this.config;
        const { minCharsType = false } = this.config;
        if (minChars > 0) {
            let cleanser = new RegExp(' ', 'g'); // Any whitespace characters
            if (minCharsType === 'alpha') {
                cleanser = new RegExp('[^a-zA-Z]', 'g'); // Any non-alpha characters
            }
            if (minCharsType === 'noSpecialChars' && value.match(new RegExp('[^\\w\\d\\s]', 'g'))) {
                return false;
            }
            return value.trim().replace(cleanser, '').length >= minChars;
        }
        return true;
    };

    /**
     * AutoSuggest will call this function to load up options from props and filter via a value
     * @param {String} value The value of the input
     */
    onSuggestionsFetchRequested = ({ value }) => {
        this.setOptionsFromProps(value.trim().toLowerCase());
    };

    /**
     * AutoSuggest will call this function every time you need to clear options.
     */
    onSuggestionsClearRequested = () => {
        this.setState({ options: [] });
    };

    /**
     * Renders an input element based on the supplied props
     * @returns {Node} The autosuggest element
     */
    render = () => {
        const { loading, optionSelected, options, value } = this.state;
        const { schema, manualLoading } = this.props;
        const elementId = schema.identifier;
        const UIComponentPrefix = schema.field.UIComponent || 'input_medium';
        const UIComponent = `${UIComponentPrefix}${getComponentStateSuffix(this.props)}`;
        const isLoading = ((loading && !optionSelected) && !options.length) || manualLoading;
        // Switch onSuggestionsFetchRequested function depending on if the input options are persisted (Loaded from api once)
        const onSuggestionsFetchRequested = schema.config.optionsPersist ? this.onSuggestionsFetchRequested : this.debouncedLoadOptions;
        // Need to pass the styles down to theme for this particular component.
        styles.input = classNames(this.inputUIComponent[UIComponent], getRecordingMaskClass(schema));

        const inputProps = {
            id: elementId,
            'data-id': elementId,
            ...getDisabledProp(this.props),
            placeholder: schema.field.placeholder,
            value,
            onChange: this.onChange
        };

        // The styles passed to the theme to AutoSuggest are spread to create a new object to resolve issues in unit tests
        // During the unit tests, the styles are imported in such a way that looking up a key with no value returns the
        // key instead of undefined. So when PrettyFormat is used to generate a snapshot, it checks for a property on the object
        // to see if it's immutable. As the key is returned, it thinks the plain object is immutable and fails when trying to
        // run Immutable specific methods. Recreating the object using spread resolves the issue
        return (
            <div className={styles.root}>
                <AutoSuggest
                    suggestions={options}
                    ref={(autosuggest) => { this.autosuggest = autosuggest; } }
                    onSuggestionsFetchRequested={onSuggestionsFetchRequested}
                    getSuggestionValue={this.getSuggestionValue}
                    renderSuggestion={this.renderSuggestion}
                    onSuggestionSelected={this.onSuggestionSelected}
                    onSuggestionsClearRequested={this.onSuggestionsClearRequested}
                    shouldRenderSuggestions={this.shouldRenderSuggestions}
                    focusInputOnSuggestionClick={false}
                    inputProps={inputProps}
                    key={elementId}
                    theme={{ ...styles }}
                />
                {isLoading ? <LoadingAnimation visualProps={{ variant: 'auto_complete_combo' }} /> : null}
            </div>
        );
    };

}

_AutoCompleteCombo.propTypes = {
    schema: PropTypes.shape({
        identifier: PropTypes.string, // A unique identifier for this component and where the selected label goes on state (e.g. helpers.suburbPostCodeLabel)
        stateKey: PropTypes.string, // Added by the form element factory e.g. vertical.quote or vertical.callMeBack
        field: PropTypes.shape({
            type: PropTypes.oneOf(['autoCompleteCombo']),
            autoFocus: PropTypes.bool, // Focus input field when field is enabled (Used for Address)
            placeholder: PropTypes.string, // The placeholder attribute for the element
            debounceTime: PropTypes.number, // The amount of milliseconds to debounce the lookup by
            noResultsMessage: PropTypes.string, // A message to display if there are no results.
            UIComponent: PropTypes.string,
            dontReset: PropTypes.array // An optional array of strings corresponding to address payload keys that we don't want to reset if no values are found
        }),
        api: PropTypes.shape({
            onSetValue: PropTypes.object,
            onChange: PropTypes.object
        }),
        config: PropTypes.shape({
            targetIdentifier: PropTypes.string, // todo: document this prop
            minChars: PropTypes.number, // At what number of characters should it trigger a lookup
            minCharsType: PropTypes.string, // Char type used to determine minChars (eg address search to count alpha chars)
            labelKey: PropTypes.string,
            labelTemplate: PropTypes.string, // Template for the rendered options e.g. {suburb} {postcode}
            stateKeyMap: PropTypes.object, // An object containing stateKey: template label e.g. data.suburb: suburb, and use {suburb} in labelTemplate
            helpers: PropTypes.shape({
                triggerManualEntry: PropTypes.string, // A stateKey to set a value to if you want the manual entry to toggle off/on (address lookup specific?)
                setOptionsFrom: PropTypes.string // todo: this is in energy, but doesn't appear to be used in here anywhere?
            }),
            optionsStateKey: PropTypes.string, // A state key to an array of objects if the lookup doesn't need an API, but rather filters a local list.
            optionsLabelKey: PropTypes.string, // The key for the "label" property from the optionsStateKey array of objects
            optionsValueKey: PropTypes.string, // The key for the "value" property from the optionsStateKey array of objects,
            optionsPersist: PropTypes.bool, // Changes the onSuggestionsFetchRequested to just load options once and to show all options when AutoSuggest is empty
            valueStateKey: PropTypes.string // The state path from vertical.quote.form level to where the suggestion's value should be stored on state
        })
    }).isRequired,
    dispatch: PropTypes.func.isRequired,
    value: PropTypes.string.isRequired,
    // eslint-disable-next-line react/no-unused-prop-types
    rules: PropTypes.object.isRequired, // this is used, but not in a way that the linter can detect
    // Optional, extra args from state that may be required for the api call - eg postcode and suburb for a 'streetsuburb' lookup
    payloadArgs: PropTypes.object,
    // Optional: options can come from state, instead of the result of an API call
    options: PropTypes.array
};

/**
 * Map options and payload from state to props
 * @param {Immutable.Map} state - The application state
 * @param {Object} props - The page props
 * @returns {Object} - The options and payload state values to be added to props.
 */
const mapStateToProps = (state, props) => {
    // Necessary to clear car model input field on car make update on prefill verify page
    const onChangeStateKeys = props.schema.identifier === 'helpers.carModelInputPrefillVerify'
        ? { make: 'coverDetail.vehicle.make' ?? '' }
        : get(props, 'schema.api.onChange.extraPayload', {});
    const payloadArgs = {};
    const form = quoteFormSelector(state);
    for (const key of Object.keys(onChangeStateKeys)) {
        payloadArgs[key] = form.getIn(onChangeStateKeys[key].split('.'));
    }
    const { config } = props.schema;
    let options = [];
    if (config.optionsStateKey) {
        // looks for the rawOptions optionsStateKey in vertical.quote.form or within the entire store
        const rawOptions = get(form, config.optionsStateKey, false) || get(state, config.optionsStateKey, false);
        if (rawOptions) {
            // Note: This expects an array of objects with single key/value mapping.
            // If a template of values is required, you'd need to do similar interpolation to labelTemplate
            options = rawOptions.reduce((acc, val) => {
                acc.push({ label: val.get(config.optionsLabelKey), value: val.get(config.optionsValueKey) });
                return acc;
            }, []);
        }
    }
    return { payloadArgs, options };
};

const AutoCompleteCombo = connect(mapStateToProps)(_AutoCompleteCombo);

export { _AutoCompleteCombo, AutoCompleteCombo };
